import {
  MIXED_GST,
  TAX_STATUS_SUBFORM as TAX_STATUS,
} from '@/modules/entities/TaxStatusSubform/TaxStatusSubform.constants';
import {
  EntityGstStatus,
  EntityInEstateStatus,
  NonTrustEntityTaxStatus,
  TrustTaxStatus,
} from '@/types/schema';

import {
  EstateInclusionStatus,
  GenericNonTrustEntityTaxStatus,
  TaxStatusSubformType,
} from '../../../TaxStatusSubform/TaxStatusSubform.types';
import { SubformsCombinedType } from '../../EntitySubforms.types';

export const entityGstStatusToApiTypeMap = {
  'gst-exempt': EntityGstStatus.GstExempt,
  'gst-non-exempt': EntityGstStatus.GstNonExempt,
  'mixed-gst': EntityGstStatus.MixedGst,
};

export const trustTaxStatusToApiTypeMap = {
  'grantor-trust': TrustTaxStatus.GrantorTrust,
  'non-grantor-trust': TrustTaxStatus.NonGrantorTrust,
  'non-taxable-trust': TrustTaxStatus.NonTaxableTrust,
};

export const nonTrustEntityTaxStatusToApiTypeMap: Record<
  GenericNonTrustEntityTaxStatus,
  NonTrustEntityTaxStatus
> = {
  'taxable-entity': NonTrustEntityTaxStatus.Taxable,
  'non-taxable-entity': NonTrustEntityTaxStatus.NonTaxable,
};

export const estateInclusionStatusToApiTypeMap: Record<
  EstateInclusionStatus,
  EntityInEstateStatus
> = {
  'in-estate': EntityInEstateStatus.InEstate,
  'out-of-estate': EntityInEstateStatus.OutOfEstate,
};

/**
 * Maps the form's nullable state in-estate value to a mutation input
 * value, returning `undefined` (i.e. "don't write") for null/empty.
 * Used wherever the asset-level tax situs field flows from the form
 * into a Create/Update entity subtype input.
 */
export function mapStateInEstateStatusToApi(
  value: EstateInclusionStatus | null | undefined
): EntityInEstateStatus | undefined {
  return value ? estateInclusionStatusToApiTypeMap[value] : undefined;
}

export function getNonTrustEntityTaxStatusUpdates(
  formDetails: TaxStatusSubformType | undefined
) {
  if (!formDetails) {
    throw new Error(`Tax status subform is required`);
  }

  return {
    taxStatus: formDetails.nonTrustEntityTaxStatus
      ? nonTrustEntityTaxStatusToApiTypeMap[formDetails.nonTrustEntityTaxStatus]
      : undefined,
    gstStatus: formDetails.gstStatus
      ? entityGstStatusToApiTypeMap[formDetails.gstStatus]
      : undefined,
    inEstateStatus: formDetails.estateInclusionStatus
      ? estateInclusionStatusToApiTypeMap[formDetails.estateInclusionStatus]
      : undefined,
    stateInEstateStatus: mapStateInEstateStatusToApi(
      formDetails.stateInEstateStatus
    ),
    inclusionRatio:
      formDetails.gstStatus === MIXED_GST
        ? formDetails.inclusionRatio
        : undefined,
  };
}

export function sharedClearTaxStatusUpdate(
  formInput: Partial<SubformsCombinedType>
) {
  return {
    clearGstStatus:
      formInput[TAX_STATUS]?.gstStatus === '' ||
      formInput[TAX_STATUS]?.estateInclusionStatus === 'in-estate',
    clearTaxStatus:
      formInput[TAX_STATUS]?.nonTrustEntityTaxStatus === '' &&
      formInput[TAX_STATUS]?.taxStatus === '',
    clearInclusionRatio: formInput[TAX_STATUS]?.gstStatus !== MIXED_GST,
  };
}
